/**
 * DualgsViewer-XR-ios.jsx — iOS-only WebXR bridge via Variant Launch (LaunchAR).
 *
 * iPhone Safari has no native WebXR. Variant Launch redirects into its own
 * AR-capable browser (which injects `navigator.xr`), runs the experience, then
 * returns to our URL. This module isolates everything iOS/Variant-specific so
 * DualgsViewer-XR.jsx can stay focused on standard WebXR (controllers, hit-test,
 * touch, placement). The main file calls into these helpers:
 *
 *   - isIOSPhone()              — platform gate
 *   - loadVariantLaunchSDK()    — inject the SDK on demand (cost control)
 *   - redirectToVariantLaunchAR() — start the iOS AR redirect on a user gesture
 *   - hasAutoStartMarker()      — did we just return from the Variant browser?
 *   - clearAutoStartMarker()    — strip the marker so refresh doesn't re-trigger
 */

// Marker query param we add to the return URL so that, once Variant Launch
// brings the user back (now with native WebXR), we know to auto-start AR.
const AUTO_START_PARAM = 'instantWebxr'

// Variant Launch (LaunchAR) SDK key — only used to build the iOS AR redirect URL.
const VARIANT_LAUNCH_SDK = 'https://launchar.app/sdk/v1?key=KshF1q6Dl5WC4HwKN1skvNfA1EkgJHm8&redirect=false'

// iPhone / iPod only. iPadOS 13+ reports as Macintosh in modern Safari and
// can't be reliably distinguished without touch-event heuristics — those
// users either install a WebXR-capable browser or miss the button.
export function isIOSPhone() {
  if (typeof navigator === 'undefined') return false
  return /iPhone|iPod/.test(navigator.userAgent)
}

// Inject the Variant Launch SDK on demand instead of on every page load, so it
// only counts a "launch" when an iOS visitor actually opens an XR viewer.
// No-op when: not iOS, native WebXR already exists (e.g. inside the Variant
// browser after redirect — the SDK isn't needed there), or already loaded.
export function loadVariantLaunchSDK() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  if (!isIOSPhone()) return
  if (navigator.xr) return // WebXR-capable already → redirect SDK not needed
  if (window.VLaunch || document.getElementById('variant-launch-sdk')) return
  const s = document.createElement('script')
  s.id = 'variant-launch-sdk'
  s.src = VARIANT_LAUNCH_SDK
  s.async = true
  document.head.appendChild(s)
}

// Kick off the iOS AR redirect. Must be called from a user gesture (the click
// counts as activation, so navigation goes through). Variant Launch's SDK
// exposes `window.VLaunch.getLaunchUrl()`; we append the auto-start marker so we
// resume AR on return. Returns true if a redirect was initiated.
export function redirectToVariantLaunchAR() {
  if (typeof window === 'undefined') return false
  if (window.VLaunch && window.VLaunch.getLaunchUrl) {
    const returnUrl = new URL(window.location.href)
    returnUrl.searchParams.set(AUTO_START_PARAM, 'true')
    window.location.href = window.VLaunch.getLaunchUrl(returnUrl.toString())
    return true
  }
  alert('AR SDK not loaded yet')
  return false
}

// True when we've just returned from the Variant Launch AR browser (the URL
// carries the auto-start marker).
export function hasAutoStartMarker() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get(AUTO_START_PARAM) === 'true'
}

// Strip the marker so a manual refresh doesn't re-trigger the auto-start.
export function clearAutoStartMarker() {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  url.searchParams.delete(AUTO_START_PARAM)
  window.history.replaceState({}, '', url.toString())
}
